import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { environment } from '@g99-product-web-ui/shared-environment';
import { IAuthResponse } from '@g99-product-web-ui/shared-interface-lib';
import {
  AnalyticsProxyService,
  LocalStorageService,
  SharedBusinessService,
  SubjectManageService,
  ToastService,
} from '@g99-product-web-ui/shared-service';
import { LOCAL_STORAGE_KEYS } from '@g99-product-web-ui/shared-util-lib';
import { Subscription, take } from 'rxjs';

import { AuthenticationService } from '../../services/authentication.service';

@Component({
  selector: 'g99-support-login',
  templateUrl: './support-login.component.html',
  styleUrl: './support-login.component.scss',
  standalone: false,
})
export class SupportLoginComponent implements OnInit, OnDestroy {
  public isLoading = false;
  public icon = '<i class="pi pi-arrow-left"> </i>';
  public isSignInErroredOut = false;
  public currentMessage = 'Logging in...';
  public errorMessage = '';
  public isClimbing = false;
  private _authToken: string | null = null;
  private _subscriptions: Subscription = new Subscription();

  constructor(
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _localStorageService: LocalStorageService,
    private readonly _authService: AuthenticationService,
    private readonly _subjectManagerService: SubjectManageService,
    private readonly _sharedService: SharedBusinessService,
    private readonly _toastService: ToastService,
    private readonly _analyticsService: AnalyticsProxyService
  ) {
    this._resetAppState();
  }

  ngOnInit(): void {
    this._startClimbing();
    this._initializeSupportLogin();
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions to prevent memory leaks
    this._subscriptions.unsubscribe();
  }

  public redirectToAdminPortal() {
    const adminRoute = environment.local.adminAppUrl;
    window.location.replace(adminRoute);
  }

  /**
   * Initializes the support login process by resetting the application state,
   * storing support login type in local storage, and subscribing to route parameters
   * to retrieve the authentication token.
   */
  private _initializeSupportLogin(): void {
    this.isLoading = true;
    this._resetAppState();
    this._localStorageService.storeItem('supportLoginOrg', {
      loginType: 'support',
    });

    const routeSubscription = this._activatedRoute.paramMap.subscribe(
      (params: ParamMap) => {
        this._authToken = params.get('token');
        if (this._authToken) {
          this._performSupportLogin(this._authToken);
        }
      }
    );

    this._subscriptions.add(routeSubscription);
  }

  /**
   * Performs the support login operation using the provided authentication token.
   * Handles successful login by fetching business data and updates application state.
   * Handles errors by displaying appropriate error messages.
   *
   * @param {string} token - The authentication token required for login.
   */
  private _performSupportLogin(token: string): void {
    this._authService
      .supportLogin(token)
      .pipe(take(1))
      .subscribe({
        next: (userResponse: IAuthResponse) => {
          this.isLoading = false;
          if (userResponse?.businessId) {
            this._fetchBusinessData(userResponse);
          }
        },
        error: (error: Error) => {
          this.isLoading = false;
          this.isSignInErroredOut = true;
          this.errorMessage = error.message;
          this._toastService.showError(error.message);
        },
      });
  }

  /**
   * Fetches the business data associated with the logged-in user and updates the application state.
   * Stores the retrieved data in local storage and emits it to the relevant subjects.
   * Redirects the user to the business page on success.
   *
   * @param {IAuthResponse} userResponse - The response object containing user authentication details.
   */
  private _fetchBusinessData(userResponse: IAuthResponse): void {
    this._localStorageService.storeItem(
      LOCAL_STORAGE_KEYS.CURRENT_USER,
      userResponse
    );
    this._subjectManagerService.userSubject.next(userResponse);

    const expirationTime = Date.now() + userResponse.expiresIn * 1000;
    this._localStorageService.storeItem(
      LOCAL_STORAGE_KEYS.TOKEN_EXPIRATION,
      expirationTime
    );

    this._sharedService
      .fetchBusinessProfile(+userResponse?.businessId)
      .pipe(take(1))
      .subscribe({
        next: (businessData) => {
          this._localStorageService.storeItem(
            LOCAL_STORAGE_KEYS.BUSINESS_INFO,
            businessData
          );
          this._subjectManagerService.businessSubject.next(businessData);
          this._authService.fetchBusinessConfig(businessData.id);

          this._localStorageService.storeItem(
            'loginType',
            {
              loginType: 'support',
            },
            () => {
              this._router.navigate(['/business']);
            }
          );
        },
        error: (error: Error) => {
          this._toastService.showError(error.message);
        },
      });
  }

  /**
   * Resets the application state by clearing local storage and resetting user and business subjects.
   */
  private _resetAppState(): void {
    // Stop any heartbeat/page-tracking left running from a previous session in
    // this tab — otherwise they keep firing analytics calls after storage is
    // cleared below, using a stale/absent token.
    this._analyticsService.stopHeartbeat();
    this._analyticsService.stopPageTracking();

    this._localStorageService.clearStorage();
    this._subjectManagerService.userSubject.next(null);
    this._subjectManagerService.businessSubject.next(null);
  }

  /**
   * Used to create a dynamic and interactive user experience during the login process.
   */
  private _startClimbing(): void {
    this.isClimbing = true;
    setTimeout(() => {
      this.currentMessage = 'You are almost there!';
    }, 3000); // Change message after 3 seconds
  }
}
